/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useGetMainMenu} from '../../custom-api/hooks/use-get-main-menu'
import {motion, AnimatePresence} from 'framer-motion'

// Components
import {
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    Text,
    Flex,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import ChevronLeftIcon from '../icons/arrows/ChevronLeft'
import CloseIcon from '../icons/action/CloseIcon'
import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import {getContentstackLink} from '../../utils/contentstack-utils'

// Others
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {DrawerMenuSecondLevel} from './drawer-menu-second-level'
import {DrawerMenuFirstLevel} from './drawer-menu-first-level'
import {remCalc} from '../../theme/_utils'

/**
 * This is the navigation component used for mobile devices (phone and tablet). It's
 * main usage is to navigate from one category to the next, but also homes links to
 * support, log in and out actions, as support links.
 */

const DrawerMenu = ({isOpen, onClose = noop, onWishlistClick}) => {
    const [ariaBusy, setAriaBusy] = useState('true')
    const [selectedPrimaryCategoryId, setSelectedPrimaryCategoryId] = useState(null)

    const intl = useIntl()
    const styles = useMultiStyleConfig('DrawerMenu')
    const {data} = useGetMainMenu()

    const categoriesData = useGetCategoriesData()

    useEffect(() => {
        setAriaBusy('false')
    }, [])

    const selectedCategory = data?.primarycategories.find(
        (primarycategory) => primarycategory.uid === selectedPrimaryCategoryId,
    )

    const categoryLink =
        categoriesData && getContentstackLink(selectedCategory?.redirects, categoriesData)

    const onDrawerClose = () => {
        setSelectedPrimaryCategoryId(null)
        onClose()
    }

    const variants = {
        initial: {x: '100%'},
        enter: {x: 0, transition: {duration: 0.3}},
        exit: {x: '100%', transition: {duration: 0.3}},
    }

    const reverseVariants = {
        initial: {x: '-100%'},
        enter: {x: 0, transition: {duration: 0.3}},
        exit: {x: '-100%', transition: {duration: 0.3}},
    }

    return (
        <Drawer
            isOpen={isOpen}
            // reset selectedId when drawer is closed from the parent component
            onCloseComplete={() => setSelectedPrimaryCategoryId(null)}
            onClose={onDrawerClose}
            placement="left"
            size="full"
            blockScrollOnMount={false} // need to check if scrolling on old IOS is still an issue without this
        >
            <DrawerOverlay width="full" h="full" />
            <DrawerContent
                overflow="scroll"
                w="100vw"
                maxW="100vw"
                // Safari 15.4 and below doesn't support 100dvh, so we need to set the height to 100vh
                sx={{
                    height: '100vh',
                    maxHeight: '100vh',
                    '@supports (height: 100dvh)': {
                        height: '100dvh',
                        maxHeight: '100dvh',
                    },
                }}
            >
                <DrawerBody
                    sx={{
                        px: {base: remCalc(16), md: remCalc(25)},
                        pt: {base: remCalc(24), md: remCalc(40)},
                        // and add extra padding to be able to click the last items
                        pb: {base: remCalc(84), sm: remCalc(94), md: remCalc(94)},
                        // if 100dvh is supported, then we use the original padding
                        '@supports (height: 100dvh)': {
                            pb: {base: remCalc(16), md: remCalc(40)},
                        },
                    }}
                >
                    <DrawerHeader
                        {...styles.drawerHeader}
                        aria-label={intl.formatMessage({
                            id: 'drawer_menu.header.assistive_msg.title',
                            defaultMessage: 'Menu Drawer',
                        })}
                    >
                        <Flex
                            {...styles.breadcrumbContainer}
                            onClick={() => setSelectedPrimaryCategoryId(null)}
                        >
                            {selectedPrimaryCategoryId && (
                                <>
                                    <ChevronLeftIcon boxSize="iconsSize.medium" />
                                    <Text {...styles.breadcrumbText}>
                                        {categoryLink?.displayName}
                                    </Text>
                                </>
                            )}
                        </Flex>
                        <CloseIcon onClick={onDrawerClose} />
                    </DrawerHeader>
                    <div
                        id="category-nav"
                        aria-live="polite"
                        aria-busy={ariaBusy}
                        aria-atomic="true"
                    >
                        <AnimatePresence initial={false} mode="popLayout">
                            {data &&
                                categoriesData &&
                                (selectedCategory ? (
                                    <motion.div
                                        key="second-level"
                                        initial="initial"
                                        animate="enter"
                                        exit="exit"
                                        variants={variants}
                                    >
                                        <DrawerMenuSecondLevel
                                            categoriesData={categoriesData}
                                            categoryUrl={categoryLink?.url}
                                            secondLevel={selectedCategory?.second_level}
                                        />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="first-level"
                                        initial="initial"
                                        animate="enter"
                                        exit="exit"
                                        variants={reverseVariants}
                                    >
                                        <DrawerMenuFirstLevel
                                            primarycategories={data.primarycategories}
                                            setSelectedPrimaryCategoryId={
                                                setSelectedPrimaryCategoryId
                                            }
                                            categoriesData={categoriesData}
                                            headerLinks={
                                                data?.headerlinks ? data?.headerlinks[0]?.links : []
                                            }
                                            onWishlistClick={onWishlistClick}
                                        />
                                    </motion.div>
                                ))}
                        </AnimatePresence>
                    </div>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}

DrawerMenu.displayName = 'DrawerMenu'

DrawerMenu.propTypes = {
    /**
     * The opened state of the drawer.
     */
    isOpen: PropTypes.bool,
    /**
     * Function called when the drawer is dismissed.
     */
    onClose: PropTypes.func,
    onWishlistClick: PropTypes.func,
}

export {DrawerMenu}
